import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import type {
  PendingInputRequest,
  SubmitInputResponseArgs,
  SystemNotification,
  Turn,
} from '../../../hooks/useWorkspace';
import WorkspaceTurn from './WorkspaceTurn';

const List = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 1.25rem 0.5rem;
  display: flex;
  flex-direction: column;
`;

const SystemRow = styled.div<{ $kind: SystemNotification['kind'] }>`
  align-self: center;
  max-width: 85%;
  padding: 0.375rem 0.75rem;
  margin: 0.5rem 0;
  border-radius: 8px;
  font-size: 0.75rem;
  text-align: center;
  border: 1px solid
    ${({ $kind }) =>
      $kind === 'error'
        ? '#fecaca'
        : $kind === 'warning'
          ? '#fde68a'
          : 'rgba(59,130,246,0.2)'};
  background: ${({ $kind }) =>
    $kind === 'error'
      ? 'var(--error-subtle, #fef2f2)'
      : $kind === 'warning'
        ? 'var(--warning-subtle, #fffbeb)'
        : 'var(--info-subtle, rgba(59,130,246,0.08))'};
  color: ${({ $kind }) =>
    $kind === 'error'
      ? 'var(--error, #ef4444)'
      : $kind === 'warning'
        ? 'var(--warning, #d97706)'
        : 'var(--info, #3b82f6)'};
`;

interface WorkspaceTurnListProps {
  turns: Turn[];
  pendingInputRequest: PendingInputRequest | null;
  systemNotifications: SystemNotification[];
  onSubmitInputResponse: (args: SubmitInputResponseArgs) => void;
  /** Exposed so the banner can `scrollIntoView` the pending request's card. */
  scrollToTurnIdRef?: React.MutableRefObject<((turnId: string) => void) | null>;
}

const WorkspaceTurnList: React.FC<WorkspaceTurnListProps> = ({
  turns,
  pendingInputRequest,
  systemNotifications,
  onSubmitInputResponse,
  scrollToTurnIdRef,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastTurn =
    turns.length > 0 ? turns[turns.length - 1] : undefined;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [
    turns.length,
    lastTurn?.activities.length,
    lastTurn?.assistantContent,
    systemNotifications.length,
  ]);

  useEffect(() => {
    if (!scrollToTurnIdRef) return;
    scrollToTurnIdRef.current = (turnId: string) => {
      const el = containerRef.current?.querySelector(
        `[data-turn-id="${turnId}"]`,
      );
      if (el && 'scrollIntoView' in el) {
        (el as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    };
    return () => {
      if (scrollToTurnIdRef.current) scrollToTurnIdRef.current = null;
    };
  }, [scrollToTurnIdRef]);

  const lastTurnId = lastTurn?.id;

  return (
    <List ref={containerRef}>
      {turns.map((turn) => (
        <WorkspaceTurn
          key={turn.id}
          turn={turn}
          isPendingInput={pendingInputRequest?.turnId === turn.id}
          isLast={turn.id === lastTurnId}
          onSubmitInputResponse={onSubmitInputResponse}
        />
      ))}
      {systemNotifications.map((n) => (
        <SystemRow key={n.id} $kind={n.kind}>
          {n.content}
        </SystemRow>
      ))}
      <div ref={bottomRef} />
    </List>
  );
};

export default WorkspaceTurnList;
